/**
 * Haptic feedback composable. No-ops gracefully on web.
 */
const isCapacitor = () =>
  typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.() === true;

let hapticsModule = null;
let loadPromise = null;

// The Capacitor plugin proxy is "thenable" because it intercepts every
// property access — returning it from an async function makes Promise
// resolution invoke the proxy's .then, which the native bridge rejects
// as "Haptics.then() is not implemented on ios". So we keep the load
// async but expose access via a synchronous getter.
async function ensureLoaded() {
  if (!isCapacitor() || hapticsModule) return;
  if (!loadPromise) {
    loadPromise = import("@capacitor/haptics")
      .then((mod) => {
        hapticsModule = mod;
      })
      .catch(() => {});
  }
  await loadPromise;
}

const getHaptics = () => hapticsModule?.Haptics ?? null;

export function useHaptics() {
  const impact = async (style = "Light") => {
    await ensureLoaded();
    getHaptics()?.impact({ style }).catch(() => {});
  };

  const notification = async (type = "SUCCESS") => {
    await ensureLoaded();
    getHaptics()?.notification({ type }).catch(() => {});
  };

  const selectionStart = async () => {
    await ensureLoaded();
    getHaptics()?.selectionStart().catch(() => {});
  };

  return {
    impact,
    notification,
    impactLight: () => impact("Light"),
    impactMedium: () => impact("Medium"),
    impactHeavy: () => impact("Heavy"),
    notifySuccess: () => notification("SUCCESS"),
    notifyError: () => notification("ERROR"),
    notifyWarning: () => notification("WARNING"),
    selectionChanged: selectionStart,
  };
}
